import { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import 'leaflet/dist/leaflet.css';

const regions = [
  { id: 'lake-country',  name: 'Lake Country',         tagline: 'North Okanagan gems',      lat: 50.030, lng: -119.38 },
  { id: 'kelowna',       name: 'Kelowna',              tagline: 'Urban wine experience',     lat: 49.905, lng: -119.40 },
  { id: 'west-kelowna',  name: 'West Kelowna',         tagline: 'Westside wine trail',       lat: 49.845, lng: -119.65 },
  { id: 'peachland',     name: 'Peachland',            tagline: 'Lakeshore wine country',    lat: 49.770, lng: -119.73 },
  { id: 'summerland',    name: 'Summerland',           tagline: 'Organic wine hub',          lat: 49.600, lng: -119.70 },
  { id: 'naramata',      name: 'Naramata',             tagline: 'Naramata Bench',            lat: 49.545, lng: -119.56 },
  { id: 'penticton',     name: 'Penticton',            tagline: 'Between two lakes',         lat: 49.498, lng: -119.60 },
  { id: 'kaleden',       name: 'Kaleden',              tagline: 'Hidden gem wine country',   lat: 49.435, lng: -119.62 },
  { id: 'skaha-bench',   name: 'Skaha Bench',          tagline: 'Emerging wine frontier',    lat: 49.390, lng: -119.53 },
  { id: 'ok-falls',      name: 'OK Falls',             tagline: 'Okanagan Falls wineries',   lat: 49.328, lng: -119.56 },
  { id: 'keremeos',      name: 'Keremeos',             tagline: 'Similkameen Valley',        lat: 49.210, lng: -119.83 },
  { id: 'oliver',        name: 'Oliver / Golden Mile Bench', tagline: 'Wine Capital of Canada',    lat: 49.180, lng: -119.55 },
  { id: 'osoyoos',       name: 'Osoyoos / Black Sage', tagline: 'Desert wine country',       lat: 49.032, lng: -119.47 },
];

function RegionMap({ selectedId, onSelect }) {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;

    // Dynamically import Leaflet to avoid SSR issues
    import('leaflet').then(L => {
      const map = L.map(mapRef.current, {
        center: [49.45, -119.60],
        zoom: 9,
        zoomControl: false,
        attributionControl: false,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 13,
      }).addTo(map);

      L.control.zoom({ position: 'bottomright' }).addTo(map);

      regions.forEach(region => {
        const isSelected = region.id === selectedId;
        const icon = L.divIcon({
          className: '',
          html: `<div style="display:flex;align-items:center;gap:5px;"><div style="width:5px;height:5px;background:#722F37;border-radius:50%;border:1px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);flex-shrink:0;"></div><div class="region-pill${isSelected ? ' selected' : ''}" data-id="${region.id}">${region.name}</div></div>`,
          iconAnchor: [0, 10],
        });

        const marker = L.marker([region.lat, region.lng], { icon })
          .addTo(map)
          .on('click', () => onSelect(region.id));

        markersRef.current.push({ id: region.id, marker, L });
      });

      leafletMapRef.current = map;
    });

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markersRef.current = [];
      }
    };
  }, []);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
}

export default function RegionSelector({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedRegion = regions.find(r => r.id === value) || regions[0];

  function handleSelect(regionId) {
    onChange(regionId);
    setIsOpen(false);
  }

  return (
    <>
      {/* Inject pill styles */}
      <style>{`
        .region-pill {
          background: rgba(114, 47, 55, 0.85);
          color: white;
          border: 1px solid rgba(255,255,255,0.35);
          border-radius: 20px;
          padding: 2px 5px;
          font-size: 8.5px;
          font-weight: 700;
          white-space: nowrap;
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
          cursor: pointer;
          font-family: -apple-system, BlinkMacSystemFont, sans-serif;
          letter-spacing: 0.1px;
          transition: background 0.15s, transform 0.15s;
        }
        .region-pill:hover {
          background: rgba(114, 47, 55, 1);
          transform: scale(1.05);
        }
        .region-pill.selected {
          background: #722F37;
          border-color: white;
          box-shadow: 0 0 0 2px white, 0 2px 10px rgba(0,0,0,0.4);
          animation: region-pulse 2s ease-out infinite;
        }
        @keyframes region-pulse {
          0%   { box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(201,162,39,0.8); }
          70%  { box-shadow: 0 0 0 2px white, 0 0 0 25px rgba(201,162,39,0); }
          100% { box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(201,162,39,0); }
        }
      `}</style>

      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "w-full flex items-center justify-between gap-3 p-3",
          "bg-white rounded-xl border border-stone-200",
          "hover:border-[#722F37]/30 transition-colors",
          isOpen && "ring-2 ring-[#722F37]/20 border-[#722F37]/30"
        )}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#722F37]/10 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-[#722F37]" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-stone-800">{selectedRegion.name}</p>
            <p className="text-sm text-stone-500">{selectedRegion.tagline}</p>
          </div>
        </div>
        <ChevronDown className="w-5 h-5 text-stone-400" />
      </button>

      {/* Map modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60"
              onClick={() => setIsOpen(false)}
            />

            {/* Sheet sliding up from bottom */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl overflow-hidden"
              style={{ height: '82vh' }}
            >
              {/* Header */}
              <div className="bg-[#722F37] px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-white font-bold text-base">Choose Your Wine Region</p>
                  <p className="text-white/70 text-xs">Tap a region on the map</p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Map */}
              <div style={{ height: 'calc(100% - 60px)' }}>
                <RegionMap selectedId={value} onSelect={handleSelect} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
